
import React, { useState } from 'react';
import { Appointment } from '../types';

interface AppointmentDetailViewProps {
  appointment: Appointment;
  onClose: () => void;
  onConfirmRedeem: () => void;
  onConfirmRefund: () => void;
}

const AppointmentDetailView: React.FC<AppointmentDetailViewProps> = ({ 
  appointment, 
  onClose, 
  onConfirmRedeem,
  onConfirmRefund 
}) => {
  const [showRedeemConfirm, setShowRedeemConfirm] = useState(false);
  const [showRefundConfirm, setShowRefundConfirm] = useState(false);
  const [isPhoneVisible, setIsPhoneVisible] = useState(false);

  const isThirdParty = appointment.orderSource.includes('券') || appointment.orderSource.includes('美团') || appointment.orderSource.includes('抖音');
  const canOperate = appointment.status === '待核销';

  return (
    <div className="absolute inset-0 z-[700] flex flex-col bg-[#F2F2F7] animate-in slide-in-from-right duration-300 pb-safe">
      {/* 标题栏 */}
      <header className="bg-white px-4 h-14 flex items-center justify-between shrink-0 border-b border-gray-100">
        <button onClick={onClose} className="p-2 -ml-2 text-gray-400 active:scale-90 transition-transform">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-[16px] font-bold text-[#333]">预约单详情</h1>
        <div className="w-10"></div>
      </header>

      <main className="flex-1 overflow-y-auto p-4 space-y-4 no-scrollbar pb-32">
        {/* 状态提示卡片 (仅非待核销状态显示) */}
        {!canOperate && (
          <div className="bg-gray-50 rounded-2xl p-4 flex items-center justify-center space-x-2 border border-gray-100">
            <span className="text-[13px] font-bold text-gray-400">当前订单状态：</span>
            <span className="text-[13px] font-black text-gray-600">{appointment.status}</span>
          </div>
        )}

        {/* 商品信息卡片 */}
        <section className="bg-white rounded-2xl p-6 space-y-5 shadow-sm border border-gray-50">
          <div className="flex items-center space-x-2">
             <div className="w-1.5 h-4 bg-[#5DA756] rounded-full"></div>
             <h3 className="text-[15px] font-black text-black">商品信息</h3>
          </div>
          <div className="space-y-4">
            <InfoItem label="商品名称" value={appointment.productName} />
            <InfoItem label="购买商品" value="2份" />
            <InfoItem label="预约门店" value={appointment.merchant} />
            <InfoItem label="预约场次" value={appointment.timeSlot} />
            <InfoItem label="预约日期" value={appointment.appointmentDate} />
            <InfoItem label="支付方式" value={appointment.orderSource} />
          </div>
        </section>

        {/* 用户信息卡片 */}
        <section className="bg-white rounded-2xl p-6 space-y-5 shadow-sm border border-gray-50">
          <div className="flex items-center space-x-2">
             <div className="w-1.5 h-4 bg-[#5DA756] rounded-full"></div>
             <h3 className="text-[15px] font-black text-black">用户信息</h3>
          </div>
          <div className="space-y-4">
            <InfoItem label="联系人" value={appointment.name.replace(/[先生女士]/g, '')} />
            <div className="flex items-center">
              <span className="text-[13px] text-gray-400 w-24 shrink-0">手机号</span>
              <div className="flex items-center space-x-2">
                <span className="text-[14px] text-gray-800 font-bold">
                  {isPhoneVisible ? appointment.phone : appointment.phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')}
                </span>
                <button onClick={() => setIsPhoneVisible(!isPhoneVisible)} className="text-gray-300">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-[13px] text-gray-400 w-24 shrink-0">备注</span>
              <p className="text-[14px] text-gray-800 font-bold leading-relaxed flex-1">
                {appointment.note || '暂无备注内容'}
              </p>
            </div>
          </div>
        </section>

        {/* 不支持退款提示：仅待核销且是第三方渠道时显示 */}
        {canOperate && isThirdParty && (
          <div className="flex items-start space-x-2 px-1 pt-2">
            <div className="w-4 h-4 rounded-full border border-gray-300 flex items-center justify-center shrink-0 mt-0.5">
               <span className="text-[10px] text-gray-400 font-black">!</span>
            </div>
            <span className="text-[12px] text-gray-400 font-medium">该单使用第三方渠道券预约，不支持核销处直接退款</span>
          </div>
        )}
      </main>

      {/* 底部按钮区域：仅待核销状态显示 */}
      {canOperate && (
        <footer className="bg-white border-t border-gray-50 p-6 flex space-x-4 shrink-0 absolute bottom-0 left-0 right-0 z-50">
          {!isThirdParty && (
            <button 
              onClick={() => setShowRefundConfirm(true)}
              className="flex-1 h-12 border-2 border-gray-100 rounded-2xl text-[15px] text-[#333] font-black active:bg-gray-50 transition-all bg-white shadow-sm"
            >
              退款
            </button>
          )}
          <button 
            onClick={() => setShowRedeemConfirm(true)}
            className="flex-[1.8] h-12 bg-[#1C1C1E] text-white rounded-2xl text-[15px] font-black shadow-xl active:scale-[0.98] transition-all"
          >
            确认核销
          </button>
        </footer>
      )}

      {/* 确认弹窗 */}
      {(showRedeemConfirm || showRefundConfirm) && (
        <div className="absolute inset-0 z-[1000] flex items-center justify-center px-8 animate-in fade-in duration-200">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-[4px]" onClick={() => {setShowRedeemConfirm(false); setShowRefundConfirm(false);}}></div>
          <div className="relative bg-white w-full rounded-[2.5rem] shadow-2xl overflow-hidden p-8 animate-in zoom-in-95 duration-200">
            <h4 className="text-[16px] text-gray-900 font-black mb-10 leading-relaxed text-center">
              {showRedeemConfirm ? '确定要核销该预约单吗？' : '确定退款吗？退款后，金额将按原路退回。'}
            </h4>
            <div className="flex space-x-4">
              <button onClick={() => {setShowRedeemConfirm(false); setShowRefundConfirm(false);}} className="flex-1 py-3 bg-gray-50 rounded-2xl text-[14px] font-black text-gray-400 active:bg-gray-100 transition-colors">取消</button>
              <button onClick={showRedeemConfirm ? onConfirmRedeem : onConfirmRefund} className="flex-1 py-3 bg-[#5DA756] rounded-2xl text-[14px] font-black text-white shadow-lg shadow-[#5DA756]/20 active:opacity-90 transition-opacity">确认</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-center">
    <span className="text-[13px] text-gray-400 w-24 shrink-0">{label}</span>
    <span className="text-[14px] text-gray-800 font-bold">{value}</span>
  </div>
);

export default AppointmentDetailView;
